// modules
import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";

// utils
import { validateGroupDescription, validateGroupName } from "utils/Validations/fieldValidations";
import { formatPermissionList } from "utils/formatPermissionList";

// redux
import { useAppSelector, useAppDispatch } from "app/hooks/hooks";
import {
	resetCreateGroup,
	getGroupPermissionList,
	createGroup,
	loadingGroupPermissionSelector,
	groupPermissionListSelector,
	hasUnaurizedErrorSelector,
	getGroupsList
} from "redux/userList/userSlice";

// components
import CountDownModal from "components/molecules/ModalContainer/CountDownModalContainer";
import Modal from "components/molecules/ModalContainer/ModalContainer";

// constants
import { NewGroup, Routes } from "constants/global";

// utils
import { invalidateCookie } from "utils/token";

export type GroupDetails = {
	name: string;
	description: string;
};

export type RoleGroupItem = {
	id: string;
	value: string;
};

export type RoleGroup = {
	title?: string;
	values?: Array<RoleGroupItem>;
};

export type GroupPermissionItem = {
	perm_id: number;
	perm_code: string;
	perm_short_desc: string;
	perm_long_desc: string;
	perm_group_code: string;
	perm_group_desc: string;
	perm_val1: number;
	perm_val2: number;
};

export type PermissionGroup = {
	title?: string;
	code?: string;
	values?: Array<GroupPermissionItem>;
	disable?: boolean;
	// Added any for override Array methods
	[x: string]: any;
};

export type FieldErrors = {
	name?: boolean;
	description?: boolean;
	permissions?: boolean;
};

export function useNewGroup() {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const [selectedRoles, setSelectedRoles] = useState<Array<string>>([]);
	const [groupDetails, setGroupDetails] = useState<GroupDetails>({
		name: "",
		description: ""
	});
	const [fieldErrors, setFieldErrors] = useState<FieldErrors>({
		name: false,
		description: false,
		permissions: false
	});

	const groupPermissionListData = useAppSelector(groupPermissionListSelector);
	const permissionsLoading = useAppSelector(loadingGroupPermissionSelector) === 1;
	const hasUnaurizedError = useAppSelector(hasUnaurizedErrorSelector);

	const isDeclarationVerificationSelected = useMemo(() => {
		return selectedRoles?.includes(NewGroup.REIMB_DECLRATION_VERIFICATION);
	}, [selectedRoles]);

	const isReimbVoucherGrpSelected = useMemo(() => {
		if (isDeclarationVerificationSelected) {
			for (let index = 0; index < NewGroup.REIMBURSEMENT_VOUCHER_GROUPS.length; index++) {
				if (selectedRoles?.includes(NewGroup.REIMBURSEMENT_VOUCHER_GROUPS[index])) {
					return true;
				}
			}
			return false;
		}

		return false;
	}, [selectedRoles, isDeclarationVerificationSelected]);

	// Change handler group name and description
	const handleGroupDetailsChange = useCallback(
		({ id, value }: { id: string; value: string }) => {
			const newData = {
				...groupDetails,
				[id]: value
			};
			setGroupDetails(newData);
		},
		[fieldErrors, groupDetails, setGroupDetails]
	);

	useEffect(() => {
		dispatch(getGroupPermissionList({}));

		return () => {
			dispatch(resetCreateGroup());
		};
	}, []);

	// Check all required fields are entered
	const isCreateGroupButtonEnabled = useMemo(() => {
		const errors = Object.values(fieldErrors)?.filter((v) => !!v);

		if (errors?.length) {
			return false;
		}

		const values = Object.values(groupDetails);

		for (let i = 0; i < values?.length; i++) {
			if (!values[i]) {
				return false;
			}
		}

		if (!selectedRoles?.length) {
			return false;
		}

		return true;
	}, [groupDetails, selectedRoles, fieldErrors]);

	const onSuccess = useCallback(() => {
		dispatch(getGroupsList({}));
		navigate(Routes.ROLE_MANAGEMENT);
	}, [navigate, dispatch, getGroupsList]);

	const onFailure = useCallback(() => {
		navigate(`${Routes.ROLE_MANAGEMENT}/${Routes.CREATE_GROUP}`);
	}, [navigate]);

	const handleGoBack = useCallback(() => {
		navigate(Routes.ROLE_MANAGEMENT);
	}, []);

	const navigateToLogin = useCallback(() => {
		invalidateCookie();
		navigate(Routes?.LOGIN, { state: { path: Routes.ROLE_MANAGEMENT } });
	}, [navigate]);

	const validateFields = useCallback(
		(submit: boolean) => {
			const fieldsToValidate = [
				{ key: "name", validator: validateGroupName },
				{ key: "description", validator: validateGroupDescription }
			];

			// reset error state before validations
			setFieldErrors({
				name: false,
				description: false,
				permissions: false
			});

			const errors: Array<{ field: string | undefined }> = [];

			fieldsToValidate.forEach((field: { key: string; validator: (args: string) => boolean }) => {
				const key = field.key as keyof GroupDetails;
				const value = groupDetails[key];
				if (value || submit) {
					const isValid = field.validator(value);

					if (!isValid) {
						errors.push({ field: field.key });
					}
				}
			});

			if (!selectedRoles.length) {
				errors.push({ field: "permissions" });
			}

			if (errors.length) {
				const errs: FieldErrors = {};
				errors.forEach((err) => {
					errs[err.field as keyof FieldErrors] = true;
				});
				setFieldErrors(errs);
				return;
			}

			return true;
		},
		[selectedRoles, groupDetails, setGroupDetails, fieldErrors, setFieldErrors, validateGroupName, validateGroupDescription]
	);

	const onCreateGroupFailure = useCallback(
		(message: string | undefined, onFailureCallback: () => void) => {
			const isDuplicateError = message?.includes("Duplicate entry");

			if (isDuplicateError) {
				CountDownModal({
					timer: 3,
					title: NewGroup.DUPLICATE_GROUP,
					onTimeComplete: onFailureCallback,
					errorIcon: true
				});
			}
		},
		[CountDownModal]
	);

	// Proceed to create group only if all required fields are entered.
	const proceedToCreateGroup = useCallback(async () => {
		if (isDeclarationVerificationSelected && !isReimbVoucherGrpSelected) {
			showModalForReimbVouGrp();
			return;
		}
		const isFieldsValid = isCreateGroupButtonEnabled && validateFields(true);

		if (isCreateGroupButtonEnabled && isFieldsValid) {
			const payload = {
				group_code: groupDetails?.name,
				group_desc: groupDetails?.description,
				permissions: selectedRoles
			};

			try {
				const { data, errors, message } = await dispatch(createGroup(payload)).unwrap();

				if (data?.group_id && !errors?.length && message) {
					setGroupDetails({
						name: "",
						description: ""
					});

					CountDownModal({
						timer: 3,
						title: NewGroup.GROUP_CREATED,
						onTimeComplete: onSuccess
					});

					return;
				}
			} catch (error) {
				// axios error
				const err = error as any;
				if (err?.response?.status === 401) {
					navigate(Routes.LOGIN);
				}

				if (err?.response?.status === 401) {
					navigateToLogin();
				}
				onCreateGroupFailure(err?.response?.data?.errors[0]?.message, onFailure);
			}
		}
	}, [isCreateGroupButtonEnabled, fieldErrors, validateFields]);

	const permissionList: PermissionGroup[] = useMemo(() => {
		if (groupPermissionListData?.data && groupPermissionListData?.data?.length > 0) {
			return formatPermissionList(groupPermissionListData?.data, isDeclarationVerificationSelected);
		}
		return [];
	}, [isDeclarationVerificationSelected, groupPermissionListData, formatPermissionList]);

	// Get all the perm codes from the formatted permission list
	const retroClaimApprovalLimitDetails = useMemo(() => {
		return permissionList?.filter((item) => item?.code === NewGroup.RETRO_CLAIM_PERM_GROUP)?.[0]?.values?.map((x) => x.perm_code);
	}, [permissionList]);

	const reimbEvApprovalLimitDetails = useMemo(() => {
		return permissionList?.filter((item) => item?.code === NewGroup.REIMB_EV_APPROVAL_PERM_GROUP)?.[0]?.values?.map((y) => y.perm_code);
	}, [permissionList]);

	const reimbApprovalLimitDetails = useMemo(() => {
		return permissionList?.filter((item) => item?.code === NewGroup.REIMB_APPROVAL_PERM_GROUP)?.[0]?.values?.map((z) => z.perm_code);
	}, [permissionList]);

	const reimbNonCashApprovalLimitDetails = useMemo(() => {
		return permissionList?.filter((item) => item?.code === NewGroup.REIMB_NON_CASH_APP_LIMIT)?.[0]?.values?.map((val) => val.perm_code);
	}, [permissionList]);

	const reimbNonMemberEvApprovalLimitDetails = useMemo(() => {
		return permissionList?.filter((item) => item?.code === NewGroup.REIMB_NON_MEMBER_EV_APP_LIMIT)?.[0]?.values?.map((val) => val.perm_code);
	}, [permissionList]);

	// This method will be used when checkboxes related to value limit are selected
	const getUpdatedSelectedRoles = (value: string) => {
		let updatedRoleList: string[] = [];
		let modifiedArr: string[] = selectedRoles;

		const limitPermCode = [
			retroClaimApprovalLimitDetails,
			reimbEvApprovalLimitDetails,
			reimbApprovalLimitDetails,
			reimbNonCashApprovalLimitDetails,
			reimbNonMemberEvApprovalLimitDetails
		];

		for (let i = 0; i < limitPermCode?.length; i++) {
			const limitCode = limitPermCode[i];
			if (limitCode?.includes(value)) {
				for (let j = 0; j < limitCode?.length; j++) {
					if (selectedRoles?.indexOf(limitCode[j]) > -1) {
						modifiedArr = modifiedArr?.filter((role) => role !== limitCode[j]);
					}
				}
				updatedRoleList = [...modifiedArr, value];
				break;
			}
		}

		return updatedRoleList;
	};

	const showModalForReimbVouGrp = useCallback(() => {
		Modal({
			title: NewGroup.DECLARE_VERIFY_MESSAGE,
			type: "confirm",
			closable: true,
			cancelButton: {
				label: "Ok",
				classes: {
					label: "back_button_label_white",
					buttonInput: "back_button_violet"
				}
			}
		});
	}, []);

	const handleOnPermissionSelect = useCallback(
		(value: string) => {
			if (selectedRoles?.indexOf(value) > -1) {
				let list = selectedRoles?.filter((role) => role !== value);
				if (value === NewGroup.REIMB_DECLRATION_VERIFICATION) {
					list = list?.filter((role) => !NewGroup.REIMBURSEMENT_VOUCHER_GROUPS?.includes(role));
				}
				if (!list.length) {
					setFieldErrors({ ...fieldErrors, permissions: true });
				}
				setSelectedRoles(list);
			} else {
				setFieldErrors({ ...fieldErrors, permissions: false });
				if (
					retroClaimApprovalLimitDetails?.includes(value) ||
					reimbEvApprovalLimitDetails?.includes(value) ||
					reimbApprovalLimitDetails?.includes(value) ||
					reimbNonCashApprovalLimitDetails?.includes(value) ||
					reimbNonMemberEvApprovalLimitDetails?.includes(value)
				) {
					setSelectedRoles(getUpdatedSelectedRoles(value));
				} else {
					setSelectedRoles([...selectedRoles, value]);
				}
				if (value === NewGroup.REIMB_DECLRATION_VERIFICATION) {
					showModalForReimbVouGrp();
				}
			}
		},
		[selectedRoles, fieldErrors, permissionList, getUpdatedSelectedRoles, setFieldErrors, setSelectedRoles, getUpdatedSelectedRoles]
	);

	useEffect(() => {
		if (hasUnaurizedError) {
			navigateToLogin();
		}
	}, [hasUnaurizedError]);

	return {
		permissionList,
		selectedRoles,
		groupDetails,
		isCreateGroupButtonEnabled,
		backgroundLoading: !!(permissionsLoading && permissionList?.length),
		loading: permissionsLoading && !permissionList?.length,
		fieldErrors,
		isDeclarationVerificationSelected,
		isReimbVoucherGrpSelected,
		onCreateGroupFailure,
		setSelectedRoles,
		validateFields,
		onSuccess,
		proceedToCreateGroup,
		handleGoBack,
		handleGroupDetailsChange,
		handleOnPermissionSelect
	};
}
